import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'promo_gallery_popup_seen';
const TIMER_DELAY = 8000;
const AUTO_SWIPE_INTERVAL = 4000;

const SLIDES = [
  {
    src: '/images/long-hair-fue-ohne-rasur.png',
    alt: 'Long Hair FUE Haartransplantation komplett ohne Rasur - Dion Hair Clinic',
    link: 'https://wa.me/491722888879?text=Hallo%2C%20ich%20interessiere%20mich%20f%C3%BCr%20die%20Long%20Hair%20FUE%20Methode.',
  },
  {
    src: '/images/ratenzahlung-haartransplantation-finanzierung.png',
    alt: 'Ratenzahlung Haartransplantation - 6 Monate zinsfrei bis 72 Monate Laufzeit',
    link: 'https://www.medipay.de/ratengenerator/index.php?i=500',
  },
  {
    src: '/images/hairsim-ki-haarsimulation-vorher-nachher.png',
    alt: 'HairSim KI-Haarsimulation - Haaransatz realistisch simuliert Vorher Nachher',
    link: 'https://hairsim.de',
  },
];

const PromoGalleryPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [current, setCurrent] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const autoRef = useRef(null);

  const show = useCallback(() => {
    // A direct section link is intentional navigation, not a promo trigger.
    if (sessionStorage.getItem(STORAGE_KEY) || window.location.hash || document.body.style.position === 'fixed') return;
    const scrollY = window.scrollY;
    setIsVisible(true);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsAnimating(true));
    });
  }, []);

  const close = useCallback(() => {
    setIsAnimating(false);
    const scrollY = document.body.style.top;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY || '0') * -1, behavior: 'instant' });
    setTimeout(() => setIsVisible(false), 350);
    sessionStorage.setItem(STORAGE_KEY, '1');
  }, []);

  // Show triggers
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(show, TIMER_DELAY);
    return () => clearTimeout(timer);
  }, [show]);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (scrollPercent > 0.3) show();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [show]);

  // Auto-swipe
  const resetAuto = useCallback(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % SLIDES.length);
    }, AUTO_SWIPE_INTERVAL);
  }, []);

  useEffect(() => {
    if (isVisible) resetAuto();
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [isVisible, resetAuto]);

  const goTo = (idx) => {
    setCurrent(idx);
    resetAuto();
  };

  const prev = () => goTo((current - 1 + SLIDES.length) % SLIDES.length);
  const next = () => goTo((current + 1) % SLIDES.length);

  // Touch swipe
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) next(); else prev();
    }
    setTouchStart(null);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9990] transition-opacity duration-350 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        } bg-black/70 backdrop-blur-sm`}
        data-testid="promo-gallery-backdrop"
      />

      {/* Popup container */}
      <div
        className="fixed inset-0 z-[9991] flex items-center justify-center p-2 sm:p-4"
        style={{ touchAction: 'none' }}
      >
        <div
          className={`relative w-full max-w-lg transition-all duration-350 ${
            isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
          data-testid="promo-gallery-popup"
        >
          {/* Close button */}
          <button
            onClick={close}
            className="absolute -top-2 -right-2 sm:top-2 sm:right-2 z-20 w-9 h-9 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/90 transition-all shadow-lg"
            data-testid="promo-gallery-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Image area */}
          <div
            className="relative overflow-hidden rounded-2xl shadow-2xl bg-black"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Slides */}
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {SLIDES.map((slide, i) => (
                <a
                  key={i}
                  href={slide.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex-shrink-0"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    className="w-full h-auto object-contain"
                    draggable={false}
                  />
                </a>
              ))}
            </div>

            {/* Nav arrows - desktop */}
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); goTo(i); }}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    i === current ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>

            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10">
              <div
                className="h-full bg-white/60 transition-all"
                style={{
                  width: `${((current + 1) / SLIDES.length) * 100}%`,
                  transition: 'width 0.5s ease',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PromoGalleryPopup;
